import { FC, useState, useCallback } from 'react';
import { Contact } from '../../../types/contact.types';
import { Modal, Typography, Table, Badge } from '@crisellevaldez/speculo-ui';
import { EditContactModal } from './EditContactModal';
import { useFirestore } from '../../../hooks/useFirestore';
import { globalSettings } from '../../../config/settings';
import { ScrollableCell } from '../../common/ScrollableCell';
import { TablePagination } from '../../common/TablePagination';
import { Tag, Calendar } from 'lucide-react';
import { useContactsCalled, ContactCallStats } from '../../../hooks/useContactsCalled';
import { getFlowTypeStyle, FlowType } from '../../../utils/badgeStyles';
import { formatDate } from '../../../utils/dateTime';
import { formatNumber } from '../../../utils/format';
import { formatPhoneNumber } from '../../../utils/formatters';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface ContactsCalledModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization_id: string;
  dateRange: DateRange;
  contactsCalled: number;
  orgTimezone?: string;
  getFilteredCalls: (
    type: 'all' | 'answered' | 'opportunities',
    sortField?: 'callTimestamp' | 'corrected_duration' | 'answered_by',
    sortDirection?: 'asc' | 'desc'
  ) => Promise<unknown[]>;
}

interface TableItem extends ContactCallStats {
  id: string;
}

export const ContactsCalledModal: FC<ContactsCalledModalProps> = ({
  isOpen,
  onClose,
  organization_id,
  dateRange,
  contactsCalled,
  orgTimezone,
  getFilteredCalls,
}) => {
  const [searchQuery, _setSearchQuery] = useState('');
  const [showAllRecords, setShowAllRecords] = useState(false);
  const { stats, setStats, loading, error: _error, hasNext, loadMore } = useContactsCalled({
    isOpen,
    organization_id,
    dateRange,
    getFilteredCalls,
    showAllRecords,
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<{ id?: string; [key: string]: unknown } | null>(null);
  const { getOne } = useFirestore(globalSettings.firebase.collections.contacts);

  const handleOpenEditModal = useCallback(
    async (contactId: string) => {
      try {
        const contact = await getOne(contactId);
        if (contact) {
          setContactToEdit(contact as { id?: string; [key: string]: unknown });
          setShowEditModal(true);
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
      }
    },
    [getOne]
  );

  const columns = [
    {
      header: 'FULL NAME',
      key: 'contactName',
      width: '300px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item: TableItem) => (
        <div className="flex items-center gap-4">
          {item.contactId ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditModal(item.contactId);
              }}
              className={`font-medium text-left text-primaryfortext ${item.contactName !== '-' ? 'underline' : ''} cursor-pointer`}
            >
              {item.contactName}
            </button>
          ) : (
            <span className="font-medium text-left text-muted-foreground">{item.contactName}</span>
          )}
        </div>
      ),
    },
    {
      header: 'PHONE NUMBER',
      key: 'phoneNumber',
      width: '180px',
      cell: (item: TableItem) => formatPhoneNumber(item.phoneNumber),
    },
    {
      header: 'EMAIL',
      key: 'email',
      width: '200px',
      cell: (item: TableItem) => item.email,
    },
    {
      header: 'TAGS',
      key: 'tags',
      width: '230px',
      cell: (item: TableItem) => (
        <ScrollableCell>
          {item.tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="flex items-center bg-[#ffe5bc] text-[#88612a] gap-1"
            >
              <Tag className="w-3 h-3" />
              {tag}
            </Badge>
          ))}
        </ScrollableCell>
      ),
    },
    {
      header: 'FLOW NAME',
      key: 'flowName',
      width: '200px',
      cell: (item: TableItem) => item.flowName,
    },
    {
      header: 'FLOW TYPE',
      key: 'flowType',
      width: '150px',
      cell: (item: TableItem) => {
        if (!item.flowType || item.flowType == '-') return '-';
        const style = getFlowTypeStyle(item.flowType as FlowType);
        return (
          <Badge className={`${style.background} ${style.text}`}>
            {item.flowType}
          </Badge>
        );
      },
    },
  ];

  const filteredData = stats
    .map((stat: ContactCallStats) => ({
      ...stat,
      id: `${stat.contactId}-${stat.flowName}-${stat.call_id}`, // Unique ID for each row
    }))
    .filter((item: TableItem) => {
      if (!searchQuery) return true;
      const search = searchQuery.toLowerCase();
      return (
        item.contactName.toLowerCase().includes(search) ||
        item.phoneNumber.includes(search) ||
        item.email.toLowerCase().includes(search) ||
        item.flowName.toLowerCase().includes(search) ||
        item.flowType.toLowerCase().includes(search)
      );
    });

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <Modal.Header>
          <div>
            <div className="flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-4">
              <Typography variant="h2">Contacts Called</Typography>
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal">
                <span>
                  <Calendar className="h-4 w-4 inline-block" />{' '}
                  {dateRange.from && dateRange.to
                    ? `${formatDate(dateRange.from, orgTimezone, 'MMM D, YYYY')} - ${formatDate(
                        dateRange.to,
                        orgTimezone,
                        'MMM D, YYYY'
                      )}`
                    : 'All Time'}
                </span>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {formatNumber(contactsCalled)} {contactsCalled === 1 ? 'record' : 'records'}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="h-[calc(100dvh-250px)] 3xl:h-[calc(600px)]">
            <Table
              data={filteredData}
              columns={columns}
              keyExtractor={(item) => `${item.contactId}-${item.flowName}-${item.call_id}`}
              className="w-full"
              loading={loading}
            />
          </div>
          <div className="border-none">
            <TablePagination
              hasNext={hasNext}
              onNextPage={loadMore}
              totalItems={stats.length}
              isLoading={loading}
              showAllRecordsOption={true}
              showAllRecords={showAllRecords}
              onShowAllRecordsChange={(checked) => setShowAllRecords(checked)}
            />
          </div>
        </Modal.Body>
      </Modal>
      <EditContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setContactToEdit(null);
        }}
        onSubmit={async () => {}}
        onSuccess={(data) => {
          const updatedStats = stats.map((stat) => {
            if (stat.contactId === contactToEdit?.id) {
              return {
                ...stat,
                contactName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || '-',
              };
            }
            return stat;
          });
          setStats(updatedStats);
        }}
        contact={contactToEdit as Contact | null}
        dateRange={dateRange}
      />
    </>
  );
};
